import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { Card, Spin, message, Modal, Form, Input, Select, Switch, Button, Tag, Tooltip, Popconfirm, theme } from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  CopyOutlined,
  DragOutlined,
} from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

export interface StateVisual {
  label: string
  color: string
  icon: React.ReactNode
  x: number
  y: number
}

export interface BaseTransition {
  id: number
  required_role_id: number
  description?: string
  conditions_json?: string
  actions_json?: string
  attivo: boolean
  ordine: number
}

export interface Role {
  id: number
  nome: string
}

export interface StateSelectOption {
  value: string | number
  label: string
  color: string
}

export interface WorkflowDiagramEditorConfig<T extends BaseTransition> {
  stateConfig: Record<string, StateVisual>
  stateKeys: string[]
  transitions: T[]
  roles: Role[]
  loading: boolean
  nodeRadius: number
  arrowheadId: string
  loadingTip: string
  listTitle: string
  editTitle: string
  createTitle: string
  getFromKey: (t: T) => string
  getToKey: (t: T) => string
  transitionsUrl: string
  stateSelectOptions: StateSelectOption[]
  fromFieldName: string
  toFieldName: string
  getAddFormValues: (from: string, to: string) => Record<string, unknown>
  getEditFormValues: (t: T) => Record<string, unknown>
  getDupFormValues: (t: T) => Record<string, unknown>
  defaultFormValues: Record<string, unknown>
  extraFormFields?: React.ReactNode
  extraTransitionTag?: (t: T) => React.ReactNode
  onRefresh: () => void
  stateOrder?: Record<string, number>
  /** Called when user finishes dragging a node — persist position to DB */
  onPositionSave?: (key: string, x: number, y: number) => void
}

// ---------------------------------------------------------------------------
// SVG geometry helpers
// ---------------------------------------------------------------------------

function computeArrowPath(
  fromCfg: { x: number; y: number },
  toCfg: { x: number; y: number },
  nodeRadius: number,
  perpScale: number,
): string {
  const dx = toCfg.x - fromCfg.x
  const dy = toCfg.y - fromCfg.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist === 0) return ''
  const nx = dx / dist
  const ny = dy / dist
  const startX = fromCfg.x + nx * nodeRadius
  const startY = fromCfg.y + ny * nodeRadius
  const endX = toCfg.x - nx * nodeRadius
  const endY = toCfg.y - ny * nodeRadius
  const midX = (startX + endX) / 2
  const midY = (startY + endY) / 2
  const ctrlX = midX + (-ny * perpScale)
  const ctrlY = midY + (nx * perpScale)
  return `M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`
}

function computeMidpoint(
  fromCfg: { x: number; y: number },
  toCfg: { x: number; y: number },
  nodeRadius: number,
  perpScale: number,
): { x: number; y: number } {
  const dx = toCfg.x - fromCfg.x
  const dy = toCfg.y - fromCfg.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist === 0) return { x: fromCfg.x, y: fromCfg.y }
  const nx = dx / dist
  const ny = dy / dist
  const startX = fromCfg.x + nx * nodeRadius
  const startY = fromCfg.y + ny * nodeRadius
  const endX = toCfg.x - nx * nodeRadius
  const endY = toCfg.y - ny * nodeRadius
  const midX = (startX + endX) / 2
  const midY = (startY + endY) / 2
  return { x: midX + (-ny * perpScale * 0.5), y: midY + (nx * perpScale * 0.5) }
}

// ---------------------------------------------------------------------------
// Edge classification
// ---------------------------------------------------------------------------

type EdgeType = 'forward' | 'backward' | 'exception'

function classifyEdge(
  fromKey: string,
  toKey: string,
  stateOrder?: Record<string, number>,
): EdgeType {
  if (!stateOrder) return 'forward'
  const fromOrd = stateOrder[fromKey] ?? 90
  const toOrd = stateOrder[toKey] ?? 90
  if (fromOrd >= 90 || toOrd >= 90) return 'exception'
  if (toOrd < fromOrd) return 'backward'
  return 'forward'
}

// ---------------------------------------------------------------------------
// Drag state type
// ---------------------------------------------------------------------------

interface DragInfo {
  key: string
  startSvgX: number
  startSvgY: number
  startNodeX: number
  startNodeY: number
  hasMoved: boolean
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function GenericWorkflowDiagramEditor<T extends BaseTransition>(
  cfg: WorkflowDiagramEditorConfig<T>,
) {
  const {
    stateConfig, stateKeys, transitions, roles, loading,
    nodeRadius, arrowheadId, loadingTip, listTitle, editTitle, createTitle,
    getFromKey, getToKey, transitionsUrl, stateSelectOptions,
    fromFieldName, toFieldName,
    getAddFormValues, getEditFormValues, getDupFormValues, defaultFormValues,
    extraFormFields, extraTransitionTag, onRefresh, stateOrder, onPositionSave,
  } = cfg

  const { token } = theme.useToken()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTransition, setEditingTransition] = useState<T | null>(null)
  const [selectedFromStatus, setSelectedFromStatus] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()
  const svgRef = useRef<SVGSVGElement>(null)

  // Node positions: in-memory override of stateConfig x,y
  // Initialized from stateConfig (which already includes saved x/y from DB)
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>({})
  const dragRef = useRef<DragInfo | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const perpScale = 28

  // Reset in-memory overrides when stateConfig changes (fresh data from DB)
  useEffect(() => {
    setNodePositions({})
  }, [stateKeys.join(',')])

  // Effective config: stateConfig merged with user-dragged positions.
  // Also adds fallback entries for any state referenced by transitions
  // but missing from stateConfig (e.g. state not in work_order_status_types).
  const effectiveConfig = useMemo(() => {
    const result: Record<string, StateVisual> = {}

    // 1. Known states from stateConfig
    for (const key of stateKeys) {
      const base = stateConfig[key]
      if (!base) continue
      const pos = nodePositions[key]
      result[key] = pos ? { ...base, x: pos.x, y: pos.y } : base
    }

    // 2. Unknown states referenced in transitions — auto-position them
    const allTransitionKeys = new Set<string>()
    for (const t of transitions) {
      allTransitionKeys.add(getFromKey(t))
      allTransitionKeys.add(getToKey(t))
    }
    let fallbackIdx = 0
    for (const key of allTransitionKeys) {
      if (result[key]) continue
      // Place unknown states below the main row
      result[key] = {
        label: key.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        color: '#fa8c16',
        icon: null,
        x: 75 + fallbackIdx * nodeRadius * 3,
        y: 500,
      }
      fallbackIdx++
    }

    return result
  }, [stateConfig, stateKeys, nodePositions, transitions, getFromKey, getToKey, nodeRadius])

  // Convert screen coords to SVG coords
  const getSvgCoords = useCallback((clientX: number, clientY: number) => {
    if (!svgRef.current) return { x: 0, y: 0 }
    const pt = svgRef.current.createSVGPoint()
    pt.x = clientX
    pt.y = clientY
    const ctm = svgRef.current.getScreenCTM()
    if (!ctm) return { x: 0, y: 0 }
    const transformed = pt.matrixTransform(ctm.inverse())
    return { x: transformed.x, y: transformed.y }
  }, [])

  // Group transitions
  const groupedTransitions = transitions.reduce((acc, t) => {
    const key = `${getFromKey(t)}->${getToKey(t)}`
    if (!acc[key]) acc[key] = []
    acc[key].push(t)
    return acc
  }, {} as Record<string, T[]>)

  // ---- drag handlers ----

  const handleNodeMouseDown = useCallback((e: React.MouseEvent, key: string) => {
    e.stopPropagation()
    const svgCoords = getSvgCoords(e.clientX, e.clientY)
    const cfg = effectiveConfig[key]
    if (!cfg) return
    dragRef.current = {
      key,
      startSvgX: svgCoords.x,
      startSvgY: svgCoords.y,
      startNodeX: cfg.x,
      startNodeY: cfg.y,
      hasMoved: false,
    }
  }, [getSvgCoords, effectiveConfig])

  const handleSvgMouseMove = useCallback((e: React.MouseEvent) => {
    const drag = dragRef.current
    if (!drag) return
    const svgCoords = getSvgCoords(e.clientX, e.clientY)
    const dx = svgCoords.x - drag.startSvgX
    const dy = svgCoords.y - drag.startSvgY
    if (!drag.hasMoved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return
    drag.hasMoved = true
    if (!isDragging) setIsDragging(true)
    const newX = drag.startNodeX + dx
    const newY = drag.startNodeY + dy
    setNodePositions(prev => ({ ...prev, [drag.key]: { x: newX, y: newY } }))
  }, [getSvgCoords, isDragging])

  const handleSvgMouseUp = useCallback((e: React.MouseEvent) => {
    const drag = dragRef.current
    if (!drag) return
    if (drag.hasMoved) {
      // Persist final position to DB via callback
      const pos = nodePositions[drag.key]
      if (pos && onPositionSave) {
        onPositionSave(drag.key, pos.x, pos.y)
      }
    } else {
      // No movement = treat as click: select for transition creation
      if (selectedFromStatus === drag.key) {
        setSelectedFromStatus(null)
      } else if (selectedFromStatus !== null) {
        openAdd(selectedFromStatus, drag.key)
        setSelectedFromStatus(null)
      } else {
        setSelectedFromStatus(drag.key)
        message.info(`Clicca su un altro nodo per creare una transizione da "${effectiveConfig[drag.key]?.label}"`)
      }
    }
    dragRef.current = null
    setIsDragging(false)
  }, [nodePositions, onPositionSave, selectedFromStatus, effectiveConfig])

  const handleSvgMouseLeave = useCallback(() => {
    const drag = dragRef.current
    if (drag?.hasMoved) {
      const pos = nodePositions[drag.key]
      if (pos && onPositionSave) {
        onPositionSave(drag.key, pos.x, pos.y)
      }
    }
    dragRef.current = null
    setIsDragging(false)
  }, [nodePositions, onPositionSave])

  const resetLayout = useCallback(() => {
    setNodePositions({})
    // Reset all positions to null in DB
    if (onPositionSave) {
      stateKeys.forEach(key => {
        const base = stateConfig[key]
        if (base) onPositionSave(key, base.x, base.y)
      })
    }
    message.success('Layout ripristinato')
  }, [onPositionSave, stateKeys, stateConfig])

  // ---- modal handlers ----

  const openAdd = (from: string, to: string) => {
    setEditingTransition(null)
    form.resetFields()
    form.setFieldsValue(getAddFormValues(from, to))
    setIsModalOpen(true)
  }

  const openEdit = (t: T) => {
    setEditingTransition(t)
    form.setFieldsValue(getEditFormValues(t))
    setIsModalOpen(true)
  }

  const openDuplicate = (t: T) => {
    setEditingTransition(null)
    form.resetFields()
    form.setFieldsValue(getDupFormValues(t))
    setIsModalOpen(true)
    message.info('Seleziona un ruolo diverso per la nuova transizione')
  }

  const handleAddNew = () => {
    setEditingTransition(null)
    form.resetFields()
    form.setFieldsValue(defaultFormValues)
    setIsModalOpen(true)
  }

  const handleSave = async (values: any) => {
    setSaving(true)
    try {
      if (editingTransition) {
        await axiosInstance.put(`${transitionsUrl}/${editingTransition.id}`, values)
        message.success('Transizione aggiornata')
      } else {
        await axiosInstance.post(transitionsUrl, values)
        message.success('Transizione creata')
      }
      setIsModalOpen(false)
      onRefresh()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      message.error(typeof detail === 'string' ? detail : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await axiosInstance.delete(`${transitionsUrl}/${id}`)
      message.success('Transizione eliminata')
      onRefresh()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      message.error(typeof detail === 'string' ? detail : 'Errore durante l\'eliminazione')
    }
  }

  // ---- render ----

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin size="large" tip={loadingTip} />
        </div>
      </Card>
    )
  }

  const activeId = arrowheadId
  const inactiveId = `${arrowheadId}-inactive`
  const backwardId = `${arrowheadId}-backward`
  const exceptionId = `${arrowheadId}-exception`

  // Compute viewBox from effective positions
  const allX = stateKeys.map(k => effectiveConfig[k]?.x ?? 0)
  const allY = stateKeys.map(k => effectiveConfig[k]?.y ?? 0)
  const svgW = Math.max(820, ...allX) + nodeRadius + 80
  const TOP_MARGIN = 180
  const bottomContent = Math.max(370, ...allY) + nodeRadius + 60
  const svgH = TOP_MARGIN + bottomContent

  return (
    <div>
      {/* Toolbar */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} size="small">
          Aggiungi Transizione
        </Button>
        <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} size="small">
          Ricarica
        </Button>
        <Button icon={<DragOutlined />} onClick={resetLayout} size="small">
          Reset Layout
        </Button>
        {selectedFromStatus !== null && (
          <Tag color="blue" closable onClose={() => setSelectedFromStatus(null)}>
            Origine: {effectiveConfig[selectedFromStatus]?.label}
          </Tag>
        )}
        {/* Legend */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, color: '#595959' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width={28} height={10}><line x1={0} y1={5} x2={28} y2={5} stroke="#1890ff" strokeWidth={2} markerEnd={`url(#${activeId})`} /></svg>
            Avanzamento
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width={28} height={10}><line x1={0} y1={5} x2={28} y2={5} stroke="#ff7a45" strokeWidth={2} strokeDasharray="4 3" /></svg>
            Arretramento
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width={28} height={10}><line x1={0} y1={5} x2={28} y2={5} stroke="#8c8c8c" strokeWidth={2} /></svg>
            Eccezione
          </span>
        </div>
      </div>

      {/* SVG Diagram */}
      <Card>
        <svg
          ref={svgRef}
          width="100%"
          height={svgH}
          viewBox={`0 ${-TOP_MARGIN} ${svgW} ${svgH}`}
          style={{
            background: token.colorFillAlter,
            borderRadius: 8,
            cursor: isDragging ? 'grabbing' : 'default',
            userSelect: 'none',
          }}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseLeave}
        >
          <defs>
            <marker id={activeId} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#1890ff" />
            </marker>
            <marker id={inactiveId} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#bbb" />
            </marker>
            <marker id={backwardId} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#ff7a45" />
            </marker>
            <marker id={exceptionId} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#8c8c8c" />
            </marker>
          </defs>

          {/* Arrows — rendered before nodes so nodes sit on top */}
          {Object.entries(groupedTransitions).map(([key, group]) => {
            const [fromKey, toKey] = key.split('->')
            const fromCfg = effectiveConfig[fromKey]
            const toCfg = effectiveConfig[toKey]
            if (!fromCfg || !toCfg) return null

            const edgeType = classifyEdge(fromKey, toKey, stateOrder)
            const fromOrd = stateOrder?.[fromKey] ?? 90
            const toOrd = stateOrder?.[toKey] ?? 90
            const stepsSpanned = (fromOrd < 90 && toOrd < 90) ? Math.abs(toOrd - fromOrd) : 1
            const numIntermediate = Math.max(0, stepsSpanned - 1)
            const effectivePerpScale =
              edgeType === 'backward' ? -(Math.min(perpScale * 2 + numIntermediate * 40, 160)) :
              edgeType === 'exception' ? perpScale * 0.8 :
              -(Math.min(perpScale + numIntermediate * 40, 130))

            const path = computeArrowPath(fromCfg, toCfg, nodeRadius, effectivePerpScale)
            const mid = computeMidpoint(fromCfg, toCfg, nodeRadius, effectivePerpScale)
            const hasActive = group.some(t => t.attivo)
            if (!path) return null

            let stroke: string
            let strokeDasharray: string | undefined
            let markerId: string
            let badgeFill: string

            if (!hasActive) {
              stroke = '#bbb'; strokeDasharray = undefined; markerId = inactiveId; badgeFill = '#bbb'
            } else if (edgeType === 'backward') {
              stroke = '#ff7a45'; strokeDasharray = '7 4'; markerId = backwardId; badgeFill = '#ff7a45'
            } else if (edgeType === 'exception') {
              stroke = '#8c8c8c'; strokeDasharray = undefined; markerId = exceptionId; badgeFill = '#8c8c8c'
            } else {
              stroke = '#1890ff'; strokeDasharray = undefined; markerId = activeId; badgeFill = '#1890ff'
            }

            return (
              <g key={key} style={{ cursor: 'pointer' }}>
                <path
                  d={path}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={2}
                  strokeDasharray={strokeDasharray}
                  markerEnd={`url(#${markerId})`}
                />
                {!isDragging && (
                  <Tooltip
                    title={
                      <div style={{ maxWidth: 300 }}>
                        <div style={{ marginBottom: 4 }}>
                          <strong>{fromCfg.label} → {toCfg.label}</strong>
                          {edgeType === 'backward' && (
                            <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>Arretramento</Tag>
                          )}
                          {edgeType === 'exception' && (
                            <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>Eccezione</Tag>
                          )}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          {group.map(t => (
                            <div key={t.id} style={{
                              marginBottom: 8, padding: '4px 8px',
                              background: 'rgba(255,255,255,0.1)', borderRadius: 4,
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                            }}>
                              <div>
                                <Tag color={t.attivo ? 'green' : 'default'} style={{ margin: 0 }}>
                                  {roles.find(r => r.id === t.required_role_id)?.nome || t.required_role_id}
                                </Tag>
                                {extraTransitionTag?.(t)}
                                <div style={{ fontSize: 11, marginTop: 2, opacity: 0.8 }}>
                                  {t.description || 'Nessuna descrizione'}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <Button size="small" type="text"
                                  icon={<EditOutlined style={{ color: '#fff' }} />}
                                  onClick={e => { e.stopPropagation(); openEdit(t) }}
                                />
                                <Button size="small" type="text"
                                  icon={<CopyOutlined style={{ color: '#52c41a' }} />}
                                  onClick={e => { e.stopPropagation(); openDuplicate(t) }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, opacity: 0.7 }}>
                          Modifica · Duplica per altro ruolo
                        </div>
                      </div>
                    }
                  >
                    <g onClick={() => openEdit(group[0])}>
                      <circle cx={mid.x} cy={mid.y} r={12} fill={badgeFill} stroke="white" strokeWidth={2} />
                      <text x={mid.x} y={mid.y + 4} textAnchor="middle" fill="white" fontSize={10} fontWeight="bold">
                        {group.length}
                      </text>
                    </g>
                  </Tooltip>
                )}
                {isDragging && (
                  <g>
                    <circle cx={mid.x} cy={mid.y} r={12} fill={badgeFill} stroke="white" strokeWidth={2} />
                    <text x={mid.x} y={mid.y + 4} textAnchor="middle" fill="white" fontSize={10} fontWeight="bold">
                      {group.length}
                    </text>
                  </g>
                )}
              </g>
            )
          })}

          {/* State nodes */}
          {stateKeys.map(key => {
            const config = effectiveConfig[key]
            if (!config) return null
            const isSelected = selectedFromStatus === key
            const isException = (stateOrder?.[key] ?? 90) >= 90
            const isDraggingThis = dragRef.current?.key === key && isDragging

            const nodeContent = (
              <g
                key={key}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                onMouseDown={e => handleNodeMouseDown(e, key)}
              >
                <circle
                  cx={config.x} cy={config.y} r={nodeRadius}
                  fill={isDraggingThis ? '#e6f7ff' : 'white'}
                  stroke={isSelected ? '#52c41a' : config.color}
                  strokeWidth={isSelected ? 4 : 2.5}
                  strokeDasharray={isException ? '6 3' : undefined}
                />
                <foreignObject
                  x={config.x - nodeRadius + 6}
                  y={config.y - nodeRadius + 6}
                  width={(nodeRadius - 6) * 2}
                  height={(nodeRadius - 6) * 2}
                  style={{ pointerEvents: 'none' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
                    <div style={{ fontSize: nodeRadius > 43 ? 20 : 18, color: config.color }}>{config.icon}</div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: config.color, marginTop: 3, lineHeight: 1.1 }}>
                      {config.label}
                    </div>
                  </div>
                </foreignObject>
              </g>
            )

            if (isDragging) return nodeContent

            return (
              <Tooltip
                key={key}
                title={
                  <div>
                    <strong>{config.label}</strong>
                    <div style={{ marginTop: 4 }}>
                      {selectedFromStatus !== null
                        ? `Clicca per creare transizione da "${effectiveConfig[selectedFromStatus]?.label}"`
                        : 'Trascina per spostare · clicca per selezionare come origine transizione'}
                    </div>
                  </div>
                }
              >
                {nodeContent}
              </Tooltip>
            )
          })}
        </svg>

        {/* Instructions */}
        <div style={{ marginTop: 16, padding: 12, background: token.colorFillAlter, borderRadius: 4, fontSize: 12 }}>
          <strong>Come usare:</strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
            <li><strong>Trascina un nodo</strong> per riposizionarlo liberamente nell'area — il layout viene salvato automaticamente</li>
            <li><strong>Clicca su un nodo</strong> per selezionarlo come origine, poi clicca su un altro nodo per creare una nuova transizione</li>
            <li><strong>Hover sul numero</strong> su una freccia per vedere tutte le transizioni e modificarle/duplicarle</li>
            <li><strong>Reset Layout</strong> riporta i nodi alle posizioni iniziali</li>
            <li>Frecce blu = avanzamento · arancione tratteggiate = arretramento · grigie = eccezione</li>
          </ul>
        </div>
      </Card>

      {/* Transition list */}
      <Card title={listTitle} style={{ marginTop: 16 }} size="small">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {transitions.map(t => (
            <Tag
              key={t.id}
              color={t.attivo ? 'blue' : 'default'}
              style={{ padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => openEdit(t)}
            >
              <span>{effectiveConfig[getFromKey(t)]?.label ?? getFromKey(t)}</span>
              <span>→</span>
              <span>{effectiveConfig[getToKey(t)]?.label ?? getToKey(t)}</span>
              <span style={{ color: '#999', fontSize: 10 }}>
                ({roles.find(r => r.id === t.required_role_id)?.nome || '?'})
              </span>
              <Tooltip title="Duplica transizione">
                <CopyOutlined style={{ color: '#1890ff', marginLeft: 4 }} onClick={e => { e.stopPropagation(); openDuplicate(t) }} />
              </Tooltip>
              <Popconfirm
                title="Eliminare questa transizione?"
                onConfirm={e => { e?.stopPropagation(); handleDelete(t.id) }}
                okText="Sì" cancelText="No"
              >
                <DeleteOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} onClick={e => e.stopPropagation()} />
              </Popconfirm>
            </Tag>
          ))}
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingTransition ? editTitle : createTitle}
        open={isModalOpen}
        onOk={() => form.submit()}
        confirmLoading={saving}
        onCancel={() => { setIsModalOpen(false); setSelectedFromStatus(null) }}
        width="min(600px, 95vw)"
      >
        <Form form={form} layout="vertical" onFinish={handleSave} initialValues={defaultFormValues}>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name={fromFieldName} label="Da Stato" rules={[{ required: true, message: 'Seleziona stato attuale' }]} style={{ flex: 1 }}>
              <Select placeholder="Seleziona stato">
                {stateSelectOptions.map(opt => (
                  <Select.Option key={String(opt.value)} value={opt.value}>
                    <Tag color={opt.color}>{opt.label}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name={toFieldName} label="A Stato" rules={[{ required: true, message: 'Seleziona stato destinazione' }]} style={{ flex: 1 }}>
              <Select placeholder="Seleziona stato">
                {stateSelectOptions.map(opt => (
                  <Select.Option key={String(opt.value)} value={opt.value}>
                    <Tag color={opt.color}>{opt.label}</Tag>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item name="required_role_id" label="Ruolo Autorizzato" rules={[{ required: true, message: 'Seleziona ruolo' }]}>
            <Select placeholder="Seleziona ruolo">
              {roles.map(r => <Select.Option key={r.id} value={r.id}>{r.nome}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Descrizione">
            <Input.TextArea placeholder="Descrizione della transizione" rows={2} />
          </Form.Item>

          {extraFormFields}

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="ordine" label="Ordine" style={{ flex: 1 }}>
              <Input type="number" placeholder="0" />
            </Form.Item>
            <Form.Item name="attivo" label="Attivo" valuePropName="checked" style={{ flex: 1 }}>
              <Switch checkedChildren="Attivo" unCheckedChildren="Disattivo" />
            </Form.Item>
          </div>

          <Form.Item name="conditions_json" label="Condizioni (JSON)">
            <Input.TextArea placeholder='{"key": "value"}' rows={2} />
          </Form.Item>

          <Form.Item name="actions_json" label="Azioni (JSON)">
            <Input.TextArea placeholder='{"send_notification": true}' rows={2} />
          </Form.Item>

          {editingTransition && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
              <Button icon={<CopyOutlined />} onClick={() => openDuplicate(editingTransition)}>
                Duplica (per altro ruolo)
              </Button>
              <Popconfirm
                title="Eliminare questa transizione?"
                onConfirm={() => { handleDelete(editingTransition.id); setIsModalOpen(false) }}
                okText="Sì" cancelText="No"
              >
                <Button danger icon={<DeleteOutlined />}>Elimina</Button>
              </Popconfirm>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  )
}
